import { InfoOutlineIcon } from "@chakra-ui/icons";
import { Badge, Box, HStack, Text, Tooltip } from "@chakra-ui/react";
import { Messages } from "../../core/api";
import Select from "../../shared/components/Select";
import { getEntityStatusColorScheme } from "../../shared/utils/get-entity-status-color-scheme";
import { AuthorizationUtilizationState } from "./authorization-utilization.types";

/** "ON_HOLD" -> "On hold" */
function formatPatientStatus(status: string): string {
  const text = status.toLowerCase().replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Current patient status shown as a color-coded badge (green active, etc.). */
export function PatientStatusBadge(props: { status: string }) {
  return (
    <Badge
      colorScheme={getEntityStatusColorScheme(props.status as Messages["PatientStatus"])}
      variant="subtle"
    >
      {formatPatientStatus(props.status)}
    </Badge>
  );
}

const AUTH_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "ENTIRE_PERIOD", label: "Entire period" },
];

/** Multi-select for authorization period type (shared across both tabs). */
export function AuthTypeSelect(props: {
  value: string[] | null;
  onChange: (value: string[] | undefined) => void;
}) {
  return (
    <Select
      label="Auth type"
      multiple={true}
      options={AUTH_TYPE_OPTIONS}
      value={props.value}
      onChange={props.onChange}
    />
  );
}

/** Minutes -> hours string (1 decimal). Unlimited (null) shows an infinity glyph. */
export function formatHours(minutes: number | null): string {
  if (minutes === null) {
    return "∞";
  }
  return (minutes / 60).toFixed(1);
}

/** Fraction -> percent string. Null (unlimited / zero authorized) shows a dash. */
export function formatPercent(fraction: number | null): string {
  if (fraction === null) {
    return "—";
  }
  return `${Math.round(fraction * 100)}%`;
}

const STATE_META: Record<AuthorizationUtilizationState, { label: string; colorScheme: string }> = {
  under_allocated: { label: "Under", colorScheme: "orange" },
  fully_allocated: { label: "Full", colorScheme: "green" },
  over_allocated: { label: "Over", colorScheme: "red" },
};

export function UtilizationStateBadge(props: { state: AuthorizationUtilizationState }) {
  const meta = STATE_META[props.state];
  return (
    <Badge colorScheme={meta.colorScheme} variant="subtle">
      {meta.label}
    </Badge>
  );
}

/** Exact-boundary state from scheduled vs authorized (matches the backend). */
export function deriveUtilizationState(
  scheduledMinutes: number,
  authorizedMinutes: number
): AuthorizationUtilizationState {
  if (scheduledMinutes < authorizedMinutes) {
    return "under_allocated";
  }
  if (scheduledMinutes > authorizedMinutes) {
    return "over_allocated";
  }
  return "fully_allocated";
}

/**
 * Authorized hours cell. When the value was reduced because the patient was on
 * hold / hospitalized, shows an info icon explaining the adjustment.
 */
export function AuthorizedHours(props: {
  authorizedMinutes: number | null;
  authorizedReductionMinutes: number;
  unavailableDays?: number;
}) {
  const hours = formatHours(props.authorizedMinutes);

  if (props.authorizedReductionMinutes <= 0) {
    return <span>{hours}</span>;
  }

  const reducedHours = (props.authorizedReductionMinutes / 60).toFixed(1);
  const daysText =
    props.unavailableDays !== undefined
      ? ` — patient on hold/hospitalized ${props.unavailableDays} ${
          props.unavailableDays === 1 ? "day" : "days"
        } in range`
      : " for hold/hospitalization in range";

  return (
    <HStack display="inline-flex" spacing={1}>
      <Text as="span">{hours}</Text>
      <Tooltip label={`Adjusted -${reducedHours} h${daysText}`}>
        <InfoOutlineIcon boxSize="12px" color="orange.500" />
      </Tooltip>
    </HStack>
  );
}

const STATE_BAR_COLOR: Record<AuthorizationUtilizationState, string> = {
  under_allocated: "orange.400",
  fully_allocated: "green.400",
  over_allocated: "red.400",
};

/**
 * Inline utilization meter: fill = scheduled / authorized, colored by state.
 * Over-allocated fills to 100% (the red color conveys the overflow). Shows the
 * percent to the right and full hours in a tooltip.
 */
export function UtilizationBar(props: {
  scheduledMinutes: number;
  authorizedMinutes: number | null;
  state: AuthorizationUtilizationState;
}) {
  const { scheduledMinutes, authorizedMinutes, state } = props;
  const fraction =
    authorizedMinutes !== null && authorizedMinutes > 0
      ? scheduledMinutes / authorizedMinutes
      : null;
  const widthPct = fraction === null ? 0 : Math.min(100, Math.round(fraction * 100));
  const authLabel = authorizedMinutes === null ? "∞" : `${(authorizedMinutes / 60).toFixed(1)}h`;

  return (
    <HStack minW="120px" spacing={2}>
      <Tooltip
        label={`${(scheduledMinutes / 60).toFixed(1)}h scheduled of ${authLabel} authorized`}
      >
        <Box
          bg="gray.100"
          borderRadius="full"
          flex="1"
          h="8px"
          minW="60px"
          overflow="hidden"
          position="relative"
        >
          <Box
            bg={STATE_BAR_COLOR[state]}
            borderRadius="full"
            h="100%"
            left={0}
            position="absolute"
            top={0}
            w={`${widthPct}%`}
          />
        </Box>
      </Tooltip>
      <Text color="gray.600" fontSize="xs" minW="34px" textAlign="right">
        {fraction === null ? "—" : `${Math.round(fraction * 100)}%`}
      </Text>
    </HStack>
  );
}

/**
 * Signed remaining hours = authorized − scheduled.
 *   - positive → recoverable (unscheduled) hours, shown in orange
 *   - negative → over-allocated by that many hours, shown red with a "-"
 *   - unlimited authorization → "∞"
 */
export function UtilizationRemaining(props: {
  authorizedMinutes: number | null;
  scheduledMinutes: number;
}) {
  const { authorizedMinutes, scheduledMinutes } = props;

  if (authorizedMinutes === null) {
    return (
      <Text as="span" color="gray.500">
        ∞
      </Text>
    );
  }

  const remainingHours = (authorizedMinutes - scheduledMinutes) / 60;

  if (remainingHours < 0) {
    return (
      <Tooltip label="Over-allocated: scheduled exceeds authorized">
        <Text as="span" color="red.500" fontWeight="medium">
          -{Math.abs(remainingHours).toFixed(1)}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text as="span" color={remainingHours > 0 ? "orange.700" : "gray.600"}>
      {remainingHours.toFixed(1)}
    </Text>
  );
}

const UTILIZATION_STATE_OPTIONS: {
  value: AuthorizationUtilizationState;
  label: string;
}[] = [
  { value: "under_allocated", label: "Under-allocated" },
  { value: "fully_allocated", label: "Fully allocated" },
  { value: "over_allocated", label: "Over-allocated" },
];

/** Multi-select for the utilization state (matches the other filter dropdowns). */
export function UtilizationStateSelect(props: {
  value: AuthorizationUtilizationState[] | null;
  onChange: (value: AuthorizationUtilizationState[] | undefined) => void;
}) {
  return (
    <Select
      label="State"
      multiple={true}
      options={UTILIZATION_STATE_OPTIONS}
      value={props.value}
      onChange={props.onChange}
    />
  );
}
